<template>
    <div @click.self="$emit('close')" class="backdrop-blur-[7px] fixed w-full h-[130%] top-[-70px] mt-[-10px] z-10" style="background-color: rgba(53, 53, 53, 0.10)"></div>
    <div
        class="bg-[#252526] w-[500px] rounded-[10px] p-[14px] z-20 absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] ml-[100px] border-[1px] border-[#FFF] border-opacity-10"
        style="box-shadow: 4px 4px 4px 0px #00000040;"
    >
        <div class="font-semibold text-center px-[20px] text-[16px] text-[#fff] leading-[19px] mb-[10px]">
            Завантажити файл
        </div>
        <div class="mb-[10px]">
            <div class="text-[#8C8C8C] font-medium text-[14px] mb-1">Файл</div>
            <input
                type="file"
                ref="fileInput"
                class="w-full text-[#fff] text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-[#BD9135] file:text-white file:cursor-pointer"
                @change="onFileSelect"
                :disabled="uploading"
            >
            <p v-if="selectedFile" class="text-[#8C8C8C] text-sm mt-1">{{ selectedFile?.name }}</p>
        </div>
        <div class="mb-[10px]">
            <div class="text-[#8C8C8C] font-medium text-[14px] mb-1">Опис (необов'язково)</div>
            <textarea
                v-model="description"
                class="w-full border-[1px] border-[#9E9E9E] rounded-[6px] text-[#fff] bg-[#474849] bg-opacity-20 py-[5px] pl-[5px] outline-none"
                rows="3"
                placeholder="Опис файлу"
                :disabled="uploading"
            ></textarea>
        </div>

        <div v-if="uploading" class="mb-[10px]">
            <div class="flex justify-between text-sm text-[#8C8C8C] mb-1">
                <span>{{ progressLabel }}</span>
                <span v-if="progress !== null">{{ progress }}%</span>
            </div>
            <div class="w-full h-[6px] bg-[#474849] rounded-full overflow-hidden">
                <div
                    class="h-full rounded-full transition-all duration-300"
                    :class="uploadError ? 'bg-[#DA4167]' : 'bg-[#8FC408]'"
                    :style="{ width: (progress ?? 0) + '%' }"
                ></div>
            </div>
        </div>

        <div v-if="uploadError" class="mb-[10px] text-[#DA4167] text-sm text-center">
            {{ uploadError }}
        </div>

        <div class="flex justify-center gap-3">
            <button
                @click="upload"
                :disabled="!selectedFile || uploading"
                class="bg-[#8FC408] hover:bg-[#7ab307] disabled:opacity-50 disabled:cursor-not-allowed text-white w-[140px] font-medium h-[36px] rounded-[5px]"
            >
                {{ uploading ? 'Завантаження...' : 'Завантажити' }}
            </button>
            <button
                @click="cancel"
                :disabled="uploading"
                class="bg-[#474849] hover:bg-[#5a5a5b] disabled:opacity-50 text-white w-[140px] font-medium h-[36px] rounded-[5px]"
            >
                Скасувати
            </button>
        </div>
    </div>
</template>

<script>
    import { ref, onBeforeUnmount } from 'vue'
    import { uploadFileChunked, generateUploadId, getUploadProgress } from '../services/apiRequests'

    export default {
        name: 'upload-modal',

        emits: ['close', 'uploaded'],

        setup(props, { emit }) {
            const fileInput = ref(null)
            const selectedFile = ref(null)
            const description = ref('')
            const uploading = ref(false)
            const progress = ref(null)
            const progressLabel = ref('Підготовка...')
            const uploadError = ref('')
            let pollingTimer = null

            const stopPolling = () => {
                if (pollingTimer) {
                    clearInterval(pollingTimer)
                    pollingTimer = null
                }
            }

            onBeforeUnmount(stopPolling)

            const onFileSelect = (e) => {
                selectedFile.value = e.target.files?.[0] || null
                uploadError.value = ''
            }

            const startPolling = (uploadId) => {
                stopPolling()
                pollingTimer = setInterval(async () => {
                    try {
                        const res = await getUploadProgress(uploadId)
                        const data = res?.data
                        if (data) {
                            const pct = data.progress ?? data.percent
                            if (pct != null) {
                                progress.value = Math.round(pct)
                            }
                            if (data.status === 'done' || data.status === 'completed') {
                                progress.value = 100
                                progressLabel.value = 'Завершено'
                                stopPolling()
                            } else if (data.status === 'error' || data.status === 'failed') {
                                uploadError.value = data.detail || 'Помилка завантаження'
                                stopPolling()
                            } else {
                                progressLabel.value = 'Завантаження...'
                            }
                        }
                    } catch {
                        // polling error is non-critical, continue
                    }
                }, 700)
            }

            const upload = async () => {
                if (!selectedFile.value || uploading.value) return
                uploading.value = true
                progress.value = 0
                progressLabel.value = 'Завантаження...'
                uploadError.value = ''

                const uploadId = generateUploadId()

                startPolling(uploadId)

                try {
                    await uploadFileChunked(
                        selectedFile.value,
                        description.value,
                        uploadId,
                        (part, totalParts) => {
                            progress.value = Math.round((part / totalParts) * 100)
                        }
                    )
                    progress.value = 100
                    progressLabel.value = 'Завершено'
                    stopPolling()
                    emit('uploaded')
                    emit('close')
                } catch (err) {
                    console.error('Upload failed:', err)
                    uploadError.value = err.message || 'Помилка завантаження файлу'
                    stopPolling()
                } finally {
                    uploading.value = false
                }
            }

            const cancel = () => {
                stopPolling()
                emit('close')
            }

            return {
                fileInput,
                selectedFile,
                description,
                uploading,
                progress,
                progressLabel,
                uploadError,
                onFileSelect,
                upload,
                cancel
            }
        }
    }
</script>
